import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  ArrowLeft, Check, User, ShieldCheck, Activity, Heart, Pill,
  Stethoscope, ClipboardList, FileText, MapPin,
  Video, MessageCircle, Users, Calendar,
  X, Send, Mic, MicOff, VideoOff, MonitorUp, PhoneOff, Lock, Building2,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { getModello431 } from '@/data/modello431Data';
import EvaluationWizard from '@/components/wizard/EvaluationWizard';
import PatientPanelHeader from '@/components/PatientPanelHeader';

/* (Wizard data & component moved to /components/wizard/EvaluationWizard.jsx) */

/* (BodySilhouette & TouchOption moved to EvaluationWizard) */

/* ═══ Main Component ═══ */
/* ═══ Provenienza Labels ═══ */
const provenienzaLabels = { mmg: 'MMG / PLS (PUA)', ospedale: 'Ospedale', struttura: 'Struttura / RSA', domicilio: 'Domicilio' };

/* ═══ ReadOnly Mini-Components ═══ */
function ReadOnlyField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-1.5 border border-border/40">{value || '—'}</div>
    </div>
  );
}

function ReadOnlyCheckbox({ label, checked }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
        {checked && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
      </div>
      <span className={`text-xs ${checked ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{label}</span>
    </div>
  );
}

function ReadOnlyRadio({ value, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <RadioGroupItem value={value} disabled className="h-3.5 w-3.5" />
      <span className="text-xs text-foreground">{label}</span>
    </div>
  );
}

function SectionHeading({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-2.5 mt-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-primary" />}
      <h4 className="text-[11px] font-bold text-foreground uppercase tracking-wide">{children}</h4>
    </div>
  );
}

/* ═══ Mock Equipe Members ═══ */
const equipeMembers = [
  { id: 1, nome: 'Dr. Marco Vitale', ruolo: 'Geriatra', specialita: 'Medicina Geriatrica', avatar: 'MV', online: true },
  { id: 2, nome: 'Dr.ssa Elena Moretti', ruolo: 'Fisiatra', specialita: 'Medicina Riabilitativa', avatar: 'EM', online: true },
  { id: 3, nome: 'Anna Ferretti', ruolo: 'Infermiera', specialita: 'Infermieristica Territoriale', avatar: 'AF', online: false },
  { id: 4, nome: 'Mario Conti', ruolo: 'Assistente Sociale', specialita: 'Servizi Sociali ASL', avatar: 'MC', online: true },
];

const chatMessages = [
  { id: 1, sender: 'Dr. Marco Vitale', ruolo: 'Geriatra', avatar: 'MV', text: 'Buongiorno colleghi, ho notato dalla cartella che il paziente presenta un Parkinsonismo atipico. Suggerisco attenzione particolare alla valutazione motoria e all\'equilibrio.', time: '10:30', self: false },
  { id: 2, sender: 'Anna Ferretti', ruolo: 'Infermiera', avatar: 'AF', text: 'Ricevuto Dottore. Porto la scala Tinetti per valutazione rischio cadute. Ho anche preparato il kit per rilevazione parametri vitali.', time: '10:32', self: false },
  { id: 3, sender: 'Mario Conti', ruolo: 'Ass. Sociale', avatar: 'MC', text: 'Ho verificato la situazione sociale: il caregiver principale e la figlia, presente in casa 5 giorni su 7. Potrebbe servire supporto domiciliare nei weekend.', time: '10:35', self: false },
  { id: 4, sender: 'Tu', ruolo: 'Medico UVMD', avatar: 'TU', text: 'Perfetto, grazie a tutti. Ci aggiorniamo post-valutazione per definire il piano assistenziale.', time: 'Adesso', self: true },
];

/* ═══ Chat Drawer ═══ */
function ChatDrawer({ open, onClose, patientName }) {
  const [msg, setMsg] = useState('');

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="chat-drawer-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Drawer Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="relative w-full max-w-md bg-card border-l border-border shadow-elevated flex flex-col h-full"
        data-testid="chat-drawer"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border shrink-0">
          <div className="w-9 h-9 rounded-full bg-info/10 flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-info" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground">Equipe: {patientName}</p>
            <p className="text-xs text-muted-foreground">4 membri &middot; 3 online</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9 text-muted-foreground" data-testid="chat-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
          {/* Date separator */}
          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Oggi</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {chatMessages.map(m => (
            <div key={m.id} className={`flex gap-2.5 ${m.self ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${m.id}`}>
              {!m.self && (
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-foreground">{m.avatar}</span>
                </div>
              )}
              <div className={`max-w-[80%] ${m.self ? 'ml-auto' : ''}`}>
                {!m.self && (
                  <p className="text-[10px] font-semibold text-muted-foreground mb-0.5">{m.sender} &middot; {m.ruolo}</p>
                )}
                <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.self
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-muted/60 text-foreground rounded-bl-md'
                }`}>
                  {m.text}
                </div>
                <p className={`text-[10px] text-muted-foreground mt-1 ${m.self ? 'text-right' : ''}`}>{m.time}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-border shrink-0" data-testid="chat-input-area">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder="Scrivi un messaggio..."
              className="flex-1 h-11 px-4 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="chat-input"
            />
            <Button
              size="icon"
              className="h-11 w-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ═══ Video Modal ═══ */
function VideoModal({ open, onClose, patientName }) {
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);

  if (!open) return null;

  const participants = [
    { nome: 'Dr. Marco Vitale', ruolo: 'Geriatra', avatar: 'MV', color: 'from-blue-600/40 to-blue-900/60' },
    { nome: 'Mario Conti', ruolo: 'Assistente Sociale', avatar: 'MC', color: 'from-emerald-600/40 to-emerald-900/60' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="video-modal-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        className="relative w-full max-w-3xl bg-[#0c0c14] rounded-2xl shadow-elevated overflow-hidden"
        data-testid="video-modal"
      >
        {/* Top Bar */}
        <div className="flex items-center justify-between px-5 py-3 bg-white/5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-semibold text-white/70">Consulto pre-visita: {patientName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/40 font-mono">03:24</span>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10" data-testid="video-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Video Grid */}
        <div className="relative p-4">
          <div className="grid grid-cols-2 gap-3" data-testid="video-grid">
            {participants.map((p, i) => (
              <div key={i} className={`relative aspect-video rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center overflow-hidden`} data-testid={`video-participant-${i}`}>
                {/* Simulated video feed pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/4 left-1/3 w-24 h-32 rounded-full bg-white/20 blur-2xl" />
                  <div className="absolute bottom-1/4 right-1/3 w-20 h-28 rounded-full bg-white/15 blur-xl" />
                </div>
                <div className="text-center z-10">
                  <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center mx-auto mb-2 border border-white/10">
                    <span className="text-xl font-bold text-white">{p.avatar}</span>
                  </div>
                  <p className="text-sm font-semibold text-white">{p.nome}</p>
                  <p className="text-[10px] text-white/60">{p.ruolo}</p>
                </div>
                {/* Speaking indicator */}
                {i === 0 && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm">
                    <div className="flex gap-0.5">
                      <div className="w-0.5 h-2 bg-green-400 rounded-full animate-pulse" />
                      <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }} />
                      <div className="w-0.5 h-1.5 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    </div>
                    <span className="text-[9px] text-white/80 font-medium">Sta parlando</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* PiP: "Tu" */}
          <div className="absolute bottom-6 right-6 w-36 aspect-video rounded-xl bg-gradient-to-br from-slate-700/80 to-slate-900/90 border-2 border-white/20 shadow-lg flex items-center justify-center overflow-hidden" data-testid="video-pip">
            {camOff ? (
              <div className="text-center">
                <VideoOff className="w-5 h-5 text-white/40 mx-auto mb-1" />
                <p className="text-[9px] text-white/50">Camera off</p>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-1">
                  <span className="text-xs font-bold text-white">TU</span>
                </div>
                <p className="text-[10px] text-white/70 font-medium">Medico UVMD</p>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 px-5 py-4 bg-white/5" data-testid="video-controls">
          <button
            onClick={() => setMuted(!muted)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/15'}`}
            data-testid="video-mute-btn"
          >
            {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setCamOff(!camOff)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOff ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/15'}`}
            data-testid="video-cam-btn"
          >
            {camOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          </button>
          <button
            className="w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/15 flex items-center justify-center transition-colors"
            data-testid="video-share-btn"
          >
            <MonitorUp className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="w-14 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
            data-testid="video-hangup-btn"
          >
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </div>
  );
}


/* ═══ Collaboration Hub (2-Column Layout — Mirrors Segreteria) ═══ */
function CollaborationHub({ patient, m431, onStartWizard, onBack }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const patientFullName = `${patient.cognome} ${patient.nome}`;
  const visitTime = ['08:30', '09:00', '09:30', '10:00', '10:30', '11:00'][patient.id % 6];

  const handleCall = () => {
    setIsCalling(true);
    setTimeout(() => { setIsCalling(false); toast.success(`Chiamata a ${patientFullName} terminata`); }, 2500);
  };

  return (
    <div className="space-y-5" data-testid="collaboration-hub">
      <AnimatePresence>
        {chatOpen && <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} patientName={patientFullName} />}
        {videoOpen && <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} patientName={patientFullName} />}
      </AnimatePresence>

      {/* ── HEADER / ACTION BAR ── */}
      <div className="flex items-center gap-4 flex-wrap" data-testid="hub-header">
        <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground shrink-0" data-testid="hub-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-foreground" data-testid="hub-title">
            Preparazione Visita
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Coordinamento equipe prima della valutazione</p>
        </div>
        <Button variant="comfort" className="gap-2 h-12 px-6 text-sm font-bold rounded-xl shadow-comfort shrink-0" onClick={onStartWizard} data-testid="hub-start-evaluation-btn">
          <Stethoscope className="w-4.5 h-4.5" />
          Inizia Valutazione
        </Button>
      </div>

      {/* ── 2-COLUMN LAYOUT ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-6 items-start">

        {/* ════ LEFT COLUMN: Dati Paziente & Storico Clinico ════ */}
        <div className="min-w-0 space-y-3" data-testid="hub-left-col">
          {/* Patient Header — GlobalPatientHeader (shared across all roles) */}
          <PatientPanelHeader
            patient={patient}
            onCall={handleCall}
            isCalling={isCalling}
            onViewMore={() => {
              const el = document.getElementById('hub-acc-anagrafica');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          />

          {/* Read-Only Banner */}
          <div className="bg-muted/50 border border-border rounded-xl px-4 py-2.5 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-foreground">Modello 431 — Fascicolo Clinico</span>
            <Badge variant="secondary" className="text-[9px]">Sola Lettura</Badge>
          </div>

          {/* Accordion: Modello 431 (3 sezioni) */}
          <Accordion type="multiple" defaultValue={["hub_anagrafica"]} className="space-y-3">
            {/* ── SEZ. 1: ANAGRAFICA ── */}
            <AccordionItem value="hub_anagrafica" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="hub-acc-anagrafica">
              <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center shrink-0"><User className="w-3.5 h-3.5 text-primary" /></div>
                  <span className="text-sm font-semibold text-foreground">Sezione 1 — Dati Anagrafici e Contesto</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <SectionHeading icon={User}>Dati del Paziente</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4">
                  <ReadOnlyField label="Cognome" value={m431.anagrafica.cognome} />
                  <ReadOnlyField label="Nome" value={m431.anagrafica.nome} />
                  <ReadOnlyField label="Data di Nascita" value={m431.anagrafica.dataNascita} />
                  <ReadOnlyField label="Luogo di Nascita" value={m431.anagrafica.luogoNascita} />
                  <ReadOnlyField label="Codice Fiscale" value={m431.anagrafica.codiceFiscale} />
                  <ReadOnlyField label="Telefono" value={m431.anagrafica.telefono} />
                  <ReadOnlyField label="Via" value={m431.anagrafica.via} />
                  <ReadOnlyField label="N. Civico" value={m431.anagrafica.civico} />
                  <ReadOnlyField label="CAP" value={m431.anagrafica.cap} />
                  <ReadOnlyField label="Comune" value={m431.anagrafica.comuneResidenza} />
                  <ReadOnlyField label="Municipio" value={m431.anagrafica.municipio} />
                  <ReadOnlyField label="Domicilio (se diverso)" value={m431.anagrafica.domicilio || "Coincide con residenza"} />
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={Heart}>Persona di Riferimento</SectionHeading>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
                  <ReadOnlyField label="Relazione" value={m431.personaRiferimento.relazione} />
                  <ReadOnlyField label="Cognome" value={m431.personaRiferimento.cognome} />
                  <ReadOnlyField label="Nome" value={m431.personaRiferimento.nome} />
                  <ReadOnlyField label="Telefono" value={m431.personaRiferimento.telefono} />
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={Stethoscope}>Medico Curante (MMG)</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
                  <ReadOnlyField label="Cognome" value={m431.mmg.cognome} />
                  <ReadOnlyField label="Nome" value={m431.mmg.nome} />
                  <ReadOnlyField label="Distretto" value={m431.mmg.distretto} />
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={Building2}>Provenienza Richiesta</SectionHeading>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {Object.entries(provenienzaLabels).map(([key, label]) => (
                    <ReadOnlyCheckbox key={key} label={label} checked={m431.provenienza === key} />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── SEZ. 2: VALUTAZIONE SANITARIA ── */}
            <AccordionItem value="hub_sanitaria" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="hub-acc-sanitaria">
              <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center shrink-0"><Stethoscope className="w-3.5 h-3.5 text-primary" /></div>
                  <span className="text-sm font-semibold text-foreground">Sezione 2 — Valutazione Sanitaria</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <SectionHeading icon={ClipboardList}>Diagnosi</SectionHeading>
                <div className="space-y-2.5 mb-4">
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Diagnosi Principale</p>
                    <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.principale}</div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Concomitanti</p>
                    <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.concomitanti}</div>
                  </div>
                  {m431.diagnosi.codiciICD9 && (
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Codici ICD-9</p>
                      <div className="inline-flex flex-wrap gap-1.5">
                        {m431.diagnosi.codiciICD9.split(',').filter(Boolean).map((code, i) => (
                          <Badge key={i} variant="chip" className="text-xs font-mono">{code.trim()}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Evento acuto (90gg)?</p>
                    <RadioGroup value={m431.eventoAcuto90gg ? "si" : "no"} disabled className="flex gap-3">
                      <ReadOnlyRadio value="si" label="Si" /><ReadOnlyRadio value="no" label="No" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Trasportabile?</p>
                    <RadioGroup value={m431.trasportabile ? "si" : "no"} disabled className="flex gap-3">
                      <ReadOnlyRadio value="si" label="Si" /><ReadOnlyRadio value="no" label="No" />
                    </RadioGroup>
                  </div>
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={Activity}>Condizioni Cliniche</SectionHeading>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                  <ReadOnlyCheckbox label="Insuff. respiratoria" checked={m431.condizioniCliniche.insufficienzaRespiratoria} />
                  <ReadOnlyCheckbox label="Diabete" checked={m431.condizioniCliniche.diabete} />
                  <ReadOnlyCheckbox label="Cardiopatia" checked={m431.condizioniCliniche.cardiopatia} />
                  <ReadOnlyCheckbox label="Insuff. cardiaca" checked={m431.condizioniCliniche.insufficienzaCardiaca} />
                  <ReadOnlyCheckbox label="Malattie vascolari" checked={m431.condizioniCliniche.malattieVascolari} />
                  <ReadOnlyCheckbox label="Esiti pat. ortopediche" checked={m431.condizioniCliniche.esitiPatologieOrtopediche} />
                  <ReadOnlyCheckbox label="Mal. osteomuscolari" checked={m431.condizioniCliniche.malattieOsteomuscolari} />
                  <ReadOnlyCheckbox label="Esiti frattura femore" checked={m431.condizioniCliniche.esitiFratturaFemore} />
                  <ReadOnlyCheckbox label="Neurologiche" checked={m431.condizioniCliniche.neurologiche} />
                  <ReadOnlyCheckbox label="Mal. psichiatrica" checked={m431.condizioniCliniche.malattiaPsichiatrica} />
                  <ReadOnlyCheckbox label="Esiti di ictus" checked={m431.condizioniCliniche.esitiIctus} />
                  <ReadOnlyCheckbox label="Demenza" checked={m431.condizioniCliniche.demenza} />
                  <ReadOnlyCheckbox label="Disturbi sensoriali" checked={m431.condizioniCliniche.disturbiSensoriali} />
                  <ReadOnlyCheckbox label="Neoplasie" checked={m431.condizioniCliniche.neoplasie} />
                  <ReadOnlyCheckbox label="Altro" checked={m431.condizioniCliniche.altro} />
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={Pill}>Terapie in Atto</SectionHeading>
                <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed mb-4">{m431.terapiaInAtto || <span className="text-muted-foreground italic">Nessuna</span>}</div>
                <Separator className="my-3" />
                <SectionHeading>Condizioni Cliniche Attuali</SectionHeading>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stabilita Clinica</p>
                    <RadioGroup value={m431.stabilitaClinica} disabled className="space-y-1">
                      <ReadOnlyRadio value="stabile" label="Stabile" /><ReadOnlyRadio value="moderatamente_instabile" label="Mod. instabile" /><ReadOnlyRadio value="instabile" label="Instabile" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stato di Coscienza</p>
                    <RadioGroup value={m431.statoCoscienza} disabled className="space-y-1">
                      <ReadOnlyRadio value="lucido" label="Lucido" /><ReadOnlyRadio value="confuso" label="Confuso" /><ReadOnlyRadio value="soporoso" label="Soporoso" /><ReadOnlyRadio value="coma" label="Coma" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Cognitivi</p>
                    <RadioGroup value={m431.disturbiCognitivi} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                      <ReadOnlyRadio value="assenti" label="Assenti" /><ReadOnlyRadio value="lievi" label="Lievi" /><ReadOnlyRadio value="moderati" label="Moderati" /><ReadOnlyRadio value="gravi" label="Gravi" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Comportam.</p>
                    <RadioGroup value={m431.disturbiComportamentali} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                      <ReadOnlyRadio value="assenti" label="Assenti" /><ReadOnlyRadio value="lievi" label="Lievi" /><ReadOnlyRadio value="moderati" label="Moderati" /><ReadOnlyRadio value="gravi" label="Gravi" />
                    </RadioGroup>
                  </div>
                </div>
                <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Scala del Dolore (0-10)</p>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-muted-foreground font-mono">0</span>
                    <Slider defaultValue={[m431.dolore]} max={10} step={1} disabled className="flex-1" />
                    <span className="text-[10px] text-muted-foreground font-mono">10</span>
                    <Badge variant={m431.dolore <= 3 ? 'success' : m431.dolore <= 6 ? 'warning' : 'alta'} className="text-[10px] font-mono min-w-[28px] justify-center">{m431.dolore}</Badge>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── SEZ. 3: VALUTAZIONE SOCIALE ── */}
            <AccordionItem value="hub_sociale" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="hub-acc-sociale">
              <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center shrink-0"><Heart className="w-3.5 h-3.5 text-primary" /></div>
                  <span className="text-sm font-semibold text-foreground">Sezione 3 — Valutazione Sociale e Funzionale</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Mobilita</p>
                    <RadioGroup value={m431.mobilita} disabled className="space-y-1">
                      <ReadOnlyRadio value="indipendente" label="Indipendente" /><ReadOnlyRadio value="aiuto_persona" label="Con aiuto" /><ReadOnlyRadio value="totalmente_dipendente" label="Tot. dipendente" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Alimentazione</p>
                    <RadioGroup value={m431.alimentazione} disabled className="space-y-1">
                      <ReadOnlyRadio value="autonoma" label="Autonoma" /><ReadOnlyRadio value="assistito" label="Assistito" /><ReadOnlyRadio value="rifiuta" label="Rifiuta" /><ReadOnlyRadio value="artificiale" label="Artificiale" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Vive con:</p>
                    <RadioGroup value={m431.supportoSociale} disabled className="space-y-1">
                      <ReadOnlyRadio value="solo" label="Solo" /><ReadOnlyRadio value="coniuge" label="Coniuge" /><ReadOnlyRadio value="con_figli" label="Figli" /><ReadOnlyRadio value="genitori" label="Genitori" />
                    </RadioGroup>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Condizione Abitativa</p>
                    <RadioGroup value={m431.condizioneAbitativa} disabled className="space-y-1">
                      <ReadOnlyRadio value="idonea" label="Idonea" /><ReadOnlyRadio value="parzialmente_idonea" label="Parz. Idonea" /><ReadOnlyRadio value="non_idonea" label="Non Idonea" />
                    </RadioGroup>
                    {m431.barriereArchitettoniche && <p className="text-[10px] text-muted-foreground mt-1.5 italic">{m431.barriereArchitettoniche}</p>}
                  </div>
                </div>
                <SectionHeading icon={Activity}>Scala ADL</SectionHeading>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  <ReadOnlyCheckbox label="Allettato" checked={m431.scalaADL.allettato} />
                  <ReadOnlyCheckbox label="Carrozzina" checked={m431.scalaADL.spostaCarrozzina} />
                  <ReadOnlyCheckbox label="Deambula con aiuto" checked={m431.scalaADL.deambulaConAiuto} />
                  <ReadOnlyCheckbox label="Confuso/Disorientato" checked={m431.scalaADL.confusoDisorientato} />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── SEZ. 4: NECESSITA ASSISTENZIALI E PERCORSO ── */}
            <AccordionItem value="hub_percorso" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="hub-acc-percorso">
              <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center shrink-0"><ShieldCheck className="w-3.5 h-3.5 text-primary" /></div>
                  <span className="text-sm font-semibold text-foreground">Sezione 4 — Necessita Assistenziali e Percorso</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <SectionHeading>Trattamenti e Procedure</SectionHeading>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                  <ReadOnlyCheckbox label="Chemio/Radioterapia" checked={m431.trattamentiProcedure.chemioRadioterapia} />
                  <ReadOnlyCheckbox label="Ossigenoterapia (OLT)" checked={m431.trattamentiProcedure.ossigenoterapiaOLT} />
                  <ReadOnlyCheckbox label="Ventilazione meccanica" checked={m431.trattamentiProcedure.ventilazioneMeccanica} />
                  <ReadOnlyCheckbox label="Tracheostomia" checked={m431.trattamentiProcedure.tracheostomia} />
                  <ReadOnlyCheckbox label="Broncoaspirazione" checked={m431.trattamentiProcedure.broncoaspirazione} />
                  <ReadOnlyCheckbox label="Drenaggio posturale" checked={m431.trattamentiProcedure.drenaggioPosturale} />
                  <ReadOnlyCheckbox label="Dialisi" checked={m431.trattamentiProcedure.dialisi} />
                  <ReadOnlyCheckbox label="Trasfusioni" checked={m431.trattamentiProcedure.trasfusioniPeriodiche} />
                </div>
                <Separator className="my-3" />
                <SectionHeading>Presidi e Farmaci</SectionHeading>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                  <ReadOnlyCheckbox label="Nutr. parenterale" checked={m431.somministrazioneFarmaci.nutrizioneParenterale} />
                  <ReadOnlyCheckbox label="Nutr. SNG" checked={m431.somministrazioneFarmaci.nutrizioneEnteraleSNG} />
                  <ReadOnlyCheckbox label="Nutr. PEG" checked={m431.somministrazioneFarmaci.nutrizioneEnteralePEG} />
                  <ReadOnlyCheckbox label="Catetere vescicale" checked={m431.somministrazioneFarmaci.catetereVescicale} />
                  <ReadOnlyCheckbox label="CVC o PICC" checked={m431.somministrazioneFarmaci.cvcPicc} />
                  <ReadOnlyCheckbox label="Enterostomia" checked={m431.somministrazioneFarmaci.enterostomia} />
                  <ReadOnlyCheckbox label="Ulcere cutanee" checked={m431.somministrazioneFarmaci.ulcereCutanee} />
                  <ReadOnlyCheckbox label="Terapia infusionale" checked={m431.somministrazioneFarmaci.terapiaInfusionale} />
                  <ReadOnlyCheckbox label="ECG periodici" checked={m431.somministrazioneFarmaci.ecgPeriodici} />
                  <ReadOnlyCheckbox label="Prelievi ematici" checked={m431.somministrazioneFarmaci.prelieviEmatici} />
                </div>
                <Separator className="my-3" />
                <SectionHeading icon={FileText}>Percorso e Compilatore</SectionHeading>
                <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40 mb-3">
                  <RadioGroup value={m431.percorsoRichiesto} disabled className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
                    <ReadOnlyRadio value="riab_residenziale" label="Riab. Resid." />
                    <ReadOnlyRadio value="riab_semiresidenziale" label="Riab. Semires." />
                    <ReadOnlyRadio value="riab_domiciliare" label="Riab. Domic." />
                    <ReadOnlyRadio value="adi" label="ADI" />
                    <ReadOnlyRadio value="rsa" label="RSA" />
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                  <ReadOnlyField label="Ruolo" value={m431.compilatore.ruolo === 'mmg' ? 'MMG' : 'Medico Osped.'} />
                  <ReadOnlyField label="Nominativo" value={`${m431.compilatore.cognome} ${m431.compilatore.nome}`} />
                  <ReadOnlyField label="Telefono" value={m431.compilatore.telefono} />
                  <ReadOnlyField label="Data" value={m431.compilatore.data} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* ════ RIGHT COLUMN: Team & Contesto Operativo ════ */}
        <div className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto rounded-2xl lg:bg-muted/20 lg:border lg:border-border/60 lg:p-5 flex flex-col gap-5" data-testid="hub-right-col">
          {/* Card A: Dettagli Assegnazione */}
          <div className="bg-card rounded-xl border border-border shadow-card p-5" data-testid="hub-assignment-card">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-primary" />
              Dettagli Assegnazione
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Data e Ora Visita</p>
                    <p className="text-sm font-semibold text-foreground">{patient.dataVisitaUVMD || 'Da programmare'}, {visitTime}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Indirizzo Domicilio</p>
                    <p className="text-sm font-semibold text-foreground truncate">{patient.indirizzo}</p>
                  </div>
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(patient.indirizzo)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 hover:bg-primary/20 transition-colors"
                  data-testid="hub-map-link"
                  title="Apri in Google Maps"
                >
                  <MapPin className="w-4 h-4 text-primary" />
                </a>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Priorita</p>
                </div>
                <Badge variant={patient.priorita === 'alta' ? 'alta' : patient.priorita === 'media' ? 'media' : 'bassa'}>
                  {patient.priorita === 'alta' ? 'U-A Alta' : patient.priorita === 'media' ? 'U-B Media' : 'U-C Bassa'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                <div className="flex items-center gap-2.5">
                  <Stethoscope className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tipo Valutazione</p>
                    <p className="text-sm font-semibold text-foreground">interRAI HC</p>
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">~20 min</span>
              </div>
            </div>
          </div>

          {/* Card B: Equipe & Strumenti */}
          <div className="bg-card rounded-xl border border-border shadow-card p-5" data-testid="hub-team-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Membri Equipe
              </h3>
              <div className="flex gap-1.5">
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-info hover:bg-info/5" onClick={() => setChatOpen(true)} data-testid="hub-chat-btn">
                  <MessageCircle className="w-3.5 h-3.5" />Chat
                </Button>
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5" onClick={() => setVideoOpen(true)} data-testid="hub-video-btn">
                  <Video className="w-3.5 h-3.5" />Video
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-[16px]">
              {equipeMembers.map(member => (
                <div key={member.id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border shadow-sm bg-card" data-testid={`team-member-${member.id}`}>
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center">
                      <span className="text-[10px] font-bold text-foreground">{member.avatar}</span>
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${member.online ? 'bg-success' : 'bg-muted-foreground/40'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{member.nome}</p>
                    <p className="text-[11px] text-muted-foreground">{member.ruolo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function MedicoValutazione() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { patients, startEvaluation, completeEvaluation, saveDraft, deleteDraft } = useApp();
  const resumeDraft = location.state?.resumeDraft || null;
  const [phase, setPhase] = useState(resumeDraft ? 'wizard' : 'hub');

  const patient = useMemo(() => patients.find(p => p.id === parseInt(id)), [patients, id]);

  if (!patient) {
    return (
      <AppLayout role="medico">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Paziente non trovato</p>
        </div>
      </AppLayout>
    );
  }

  const m431 = getModello431(patient.id, patient);

  const handleSaveDraft = (formData, sectionIdx, questionIdx, completedSections) => {
    saveDraft(patient.id, formData, sectionIdx, questionIdx, completedSections);
  };

  const handleStartWizard = () => {
    startEvaluation(patient.id);
    setPhase('wizard');
  };

  /* ── Hub Phase ── */
  if (phase === 'hub') {
    return (
      <AppLayout role="medico">
        <CollaborationHub
          patient={patient}
          m431={m431}
          onStartWizard={handleStartWizard}
          onBack={() => navigate('/medico')}
        />
      </AppLayout>
    );
  }

  /* ── Wizard Phase ── */
  return (
    <AppLayout role="medico">
      <EvaluationWizard
        patient={patient}
        onBack={() => navigate('/medico')}
        completeEvaluation={(patientId) => {
          if (resumeDraft) deleteDraft(resumeDraft.id);
          completeEvaluation(patientId);
        }}
        onSaveDraft={handleSaveDraft}
        initialFormData={resumeDraft?.formData}
        initialSectionIdx={resumeDraft?.lastSectionIdx}
        initialQuestionIdx={resumeDraft?.lastQuestionIdx}
      />
    </AppLayout>
  );
}
